/**
 * Pure helpers for the team page's **waiting calls** door (#1909, 1 Oct
 * 2026; #1959). Imported on-call pins that no solved window owns yet
 * (`GET /imports/unbound-calls`) wait until those dates are solved; the
 * team page says how many are waiting and opens the roster at their dates,
 * where *Solve these dates* is offered.
 *
 * No React, no I/O. ISO date strings sort chronologically, so comparisons
 * are plain string compares. Unit-tested in `web/tests/waitingCalls.test.ts`.
 */

import { weekdayShortLabel } from "./dateWindow";
import { type DateWindow, capWindow, mondayOf } from "./rosterDatesWindow";

export interface WaitingCallsSpan {
  count: number;
  /** Earliest call date, `YYYY-MM-DD`. */
  first: string;
  /** Latest call date, `YYYY-MM-DD`. */
  last: string;
}

/** How many calls are waiting and the dates they span; `null` when none. */
export function waitingCallsSpan(calls: ReadonlyArray<{ date: string }>): WaitingCallsSpan | null {
  let first: string | null = null;
  let last: string | null = null;
  for (const { date } of calls) {
    if (first === null || date < first) first = date;
    if (last === null || date > last) last = date;
  }
  if (first === null || last === null) return null;
  return { count: calls.length, first, last };
}

/** "from Sat 3 Oct to Fri 13 Nov" / "on Sat 3 Oct" — the dates the calls
 *  span, as the door's sentence and the lead's next-steps row say them. */
export function waitingCallsDates({
  first,
  last,
}: Pick<WaitingCallsSpan, "first" | "last">): string {
  return first === last
    ? `on ${weekdayShortLabel(first)}`
    : `from ${weekdayShortLabel(first)} to ${weekdayShortLabel(last)}`;
}

/** "112 imported calls wait on dates nobody has solved yet · from Sat 3 Oct to Fri 13 Nov". */
export function waitingCallsSentence(span: WaitingCallsSpan): string {
  const calls = span.count === 1 ? "1 imported call waits" : `${span.count} imported calls wait`;
  return `${calls} on dates nobody has solved yet · ${waitingCallsDates(span)}`;
}

/**
 * The roster window the door opens: from the Monday on or before the first
 * call to the last call, cut to the roster page's longest window (62 days)
 * — the lead steps on with ▶ for calls past that.
 */
export function rosterWindowForCalls(span: Pick<WaitingCallsSpan, "first" | "last">): DateWindow {
  return capWindow({ from: mondayOf(span.first), to: span.last }).window;
}
